"use client"

/**
 * SignIn Client Component - Direct Redirect Version
 *
 * Does not display a form - directly redirects to Azure B2C authentication.
 * Azure B2C will show its own sign-in page.
 */

import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"

interface SignInClientProps {
  app?: string
  returnUrl?: string
}

export default function SignInClient({
  app,
  returnUrl: returnUrlProp,
}: SignInClientProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isRedirecting, setIsRedirecting] = useState(true)

  // Get returnUrl from props or search params
  const returnUrlParam =
    searchParams.get("returnUrl") || searchParams.get("return_url")
  const returnUrl = returnUrlProp || returnUrlParam || "/"

  // Check for error parameter
  const errorParam = searchParams.get("error")
  const forceParam = searchParams.get("force") === "true"

  useEffect(() => {
    const initiateAuth = async () => {
      try {
        // If error present, show error message but still allow redirect
        if (errorParam) {
          console.log("[SignIn] Error present:", errorParam)
          // Keep redirecting to allow retry
        }

        // Check if already authenticated
        const meResponse = await fetch("/api/me", { cache: "no-store" })

        if (meResponse.ok) {
          const { user } = await meResponse.json()

          if (user) {
            // Already authenticated, redirect to returnUrl
            console.log(
              "[SignIn] Already authenticated, redirecting to:",
              returnUrl
            )
            router.replace(returnUrl)
            return
          }
        }

        // Not authenticated - redirect to Azure B2C via GET /api/auth/signin
        // This will handle silent SSO or redirect to Azure B2C login page
        console.log("[SignIn] Not authenticated, redirecting to Azure B2C")

        const signinUrl = new URL("/api/auth/signin", window.location.origin)
        signinUrl.searchParams.set("returnUrl", returnUrl)

        if (forceParam) {
          signinUrl.searchParams.set("force", "true")
        }

        // Server-side redirect (handles silent SSO automatically)
        window.location.href = signinUrl.toString()
      } catch (error) {
        console.error("[SignIn] Error initiating auth:", error)
        setIsRedirecting(false)
      }
    }

    initiateAuth()
  }, [returnUrl, forceParam, errorParam, router])

  // Show minimal loading state during redirect
  // This should be very brief as we redirect immediately
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-sm text-muted-foreground">
          {errorParam
            ? "Authentication error occurred"
            : "Redirecting to authentication..."}
        </p>
        {errorParam && (
          <p className="mt-2 text-sm text-red-600">
            {decodeURIComponent(errorParam)}
          </p>
        )}
      </div>
    </div>
  )
}
